import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { CourseLesson, LessonProgress } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { CheckCircle, ArrowLeft, ArrowRight, FileText, Video, Download } from 'lucide-react'

export function LessonPage() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lesson, setLesson] = useState<CourseLesson | null>(null)
  const [progress, setProgress] = useState<LessonProgress | null>(null)
  const [marking, setMarking] = useState(false)
  const [allLessons, setAllLessons] = useState<CourseLesson[]>([])

  // レッスンタイトルが取得できたら動的に設定
  useDocumentTitle(lesson?.title || 'レッスン')

  useEffect(() => {
    if (!user || !courseId || !lessonId) return
    fetchLessonData()
  }, [courseId, lessonId, user])

  const fetchLessonData = async () => {
    if (!courseId || !lessonId || !user) return

    try {
      setLoading(true)
      setError(null)

      // レッスン情報を取得
      const { data: lessonData, error: lessonError } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('lesson_id', lessonId)
        .eq('course_id', courseId)
        .single()

      if (lessonError || !lessonData) {
        setError('レッスンが見つかりませんでした')
        setLoading(false)
        return
      }

      setLesson(lessonData)

      // 進捗を取得
      const { data: progressData } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('lesson_id', lessonId)
        .maybeSingle()

      if (progressData) {
        setProgress(progressData)
      }

      // 全レッスンを取得（前後移動用）
      const { data: allLessonsData } = await supabase
        .from('course_lessons')
        .select('lesson_id, title, sort_order')
        .eq('course_id', courseId)
        .order('sort_order', { ascending: true })

      if (allLessonsData) {
        setAllLessons(allLessonsData as CourseLesson[])
      }
    } catch (err: any) {
      console.error('Error fetching lesson data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsCompleted = async () => {
    if (!user || !courseId || !lessonId) return

    try {
      setMarking(true)
      setError(null)

      const { data, error: upsertError } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: user.id,
          course_id: courseId,
          lesson_id: lessonId,
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .select()
        .single()

      if (upsertError) throw upsertError

      setProgress(data)

      // 次のレッスンがあれば移動
      const currentIndex = allLessons.findIndex((l) => l.lesson_id === lessonId)
      if (currentIndex >= 0 && currentIndex < allLessons.length - 1) {
        const nextLesson = allLessons[currentIndex + 1]
        navigate(`/courses/${courseId}/lessons/${nextLesson.lesson_id}`)
      } else {
        // 最後のレッスンなら講座詳細へ
        navigate(`/courses/${courseId}`)
      }
    } catch (err: any) {
      console.error('Error marking lesson as completed:', err)
      setError(err.message || 'レッスンの完了登録に失敗しました')
    } finally {
      setMarking(false)
    }
  }

  const currentIndex = allLessons.findIndex((l) => l.lesson_id === lessonId)
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < allLessons.length - 1
  const prevLesson = hasPrev ? allLessons[currentIndex - 1] : null
  const nextLesson = hasNext ? allLessons[currentIndex + 1] : null
  const isCompleted = progress?.status === 'completed'

  if (loading) {
    return (
      <MainLayout>
        <Loading message="レッスンを読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !lesson) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || 'レッスンが見つかりませんでした'} />
          <div className="mt-4">
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              講座詳細へ戻る
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f9fafb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* ナビゲーション */}
          <div className="mb-6">
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#006cc6] transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              講座詳細へ戻る
            </button>
          </div>

          {/* レッスンヘッダー */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900 flex-1">{lesson.title}</h1>
              {isCompleted && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-sm font-medium rounded-lg border border-green-200">
                  <CheckCircle className="h-4 w-4" />
                  完了
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              {lesson.lesson_type === 'text' && (
                <>
                  <FileText className="h-4 w-4" />
                  <span>テキスト教材</span>
                </>
              )}
              {lesson.lesson_type === 'video_link' && (
                <>
                  <Video className="h-4 w-4" />
                  <span>動画教材</span>
                </>
              )}
              {lesson.lesson_type === 'pdf' && (
                <>
                  <Download className="h-4 w-4" />
                  <span>PDF教材</span>
                </>
              )}
            </div>
          </div>

          {/* レッスン内容 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            {lesson.lesson_type === 'text' && lesson.content && (
              <div className="prose prose-gray max-w-none">
                <div dangerouslySetInnerHTML={{ __html: lesson.content }} />
              </div>
            )}

            {lesson.lesson_type === 'video_link' && lesson.video_url && (
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {lesson.lesson_type === 'pdf' && lesson.content && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                  <Download className="h-8 w-8 text-[#006cc6]" />
                </div>
                <p className="text-gray-600 mb-6 font-medium">
                  PDF教材をダウンロードして学習してください
                </p>
                <a
                  href={lesson.content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#006cc6] hover:bg-[#06579e] text-white rounded-lg transition-colors font-medium shadow-sm shadow-blue-500/20"
                >
                  <Download className="h-4 w-4" />
                  PDFをダウンロード
                </a>
              </div>
            )}
          </div>

          {/* アクションボタン */}
          <div className="flex items-center justify-between gap-4 mb-6">
            {hasPrev && prevLesson ? (
              <button
                onClick={() => navigate(`/courses/${courseId}/lessons/${prevLesson.lesson_id}`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">前のレッスン</span>
                <span className="sm:hidden">前へ</span>
              </button>
            ) : (
              <div />
            )}

            {!isCompleted && (
              <button
                onClick={handleMarkAsCompleted}
                disabled={marking}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
              >
                {marking ? '処理中...' : '完了にする'}
              </button>
            )}

            {hasNext && nextLesson ? (
              <button
                onClick={() => navigate(`/courses/${courseId}/lessons/${nextLesson.lesson_id}`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#006cc6] hover:bg-[#06579e] text-white rounded-lg transition-colors font-medium shadow-sm shadow-blue-500/20"
              >
                <span className="hidden sm:inline">次のレッスン</span>
                <span className="sm:hidden">次へ</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              isCompleted && (
                <button
                  onClick={() => navigate(`/courses/${courseId}`)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#006cc6] hover:bg-[#06579e] text-white rounded-lg transition-colors font-medium shadow-sm shadow-blue-500/20"
                >
                  講座詳細へ
                  <ArrowRight className="h-4 w-4" />
                </button>
              )
            )}
          </div>

          {error && <ErrorMessage message={error} />}
        </div>
      </div>
    </MainLayout>
  )
}
